
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../axiosConfig';
import Header from '../Components/Header';
import Sidebar from '../Components/Sidebar';
import '../styles.css';

const AssignPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState({}); 
  const [currentUserId] = useState(localStorage.getItem('id')); 
  const [managers, setManagers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [locations, setLocations] = useState([]);
  const [managerId, setManagerId] = useState('');
  const [roleId, setRoleId] = useState('');
  const [activeRoleId, setActiveRoleId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [roleError, setRoleError] = useState('');
  const [rolesData, setRolesData] = useState([]);
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);

  useEffect(() => {
    const screenWidth = window.innerWidth;
    setIsSidebarVisible(screenWidth >= 520);
  }, []);

  const handleToggleClick = () => setIsSidebarVisible(!isSidebarVisible);

  useEffect(() => {
    axiosInstance.get('/user/')
      .then(response => {
        const usersData = response.data.map(user => ({
          id: user.id,
          name: `${user.first_name} ${user.last_name}`,
          role: user.role,
          employeeCode: user.employee_code
        }));
        setUsers(usersData);

        axiosInstance.get(`/user/${currentUserId}/`)
          .then(response => {
            const userData = response.data;
            if (userData.role) {
              setCurrentUser({ ...userData, role: userData.role.name });
            } else {
              const fallbackRole = userData.role?.[0]?.name || 'guest';
              setCurrentUser({ ...userData, role: fallbackRole });
            }
          })
          .catch(console.error);
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    axiosInstance.get(`/users/${currentUserId}/roles/`)
      .then(res => {
        setRolesData([res.data.active_role, res.data.assigned_role]);
      })
      .catch(console.error);
  }, []);

  const handleSwitchRole = async () => {
    try {
      await axiosInstance.post('/switch-role/', { role_id: rolesData[1].id });
      navigate('/user-dashboard');
    } catch (err) {
      setRoleError(err.response?.data?.detail || "Failed to switch role");
    }
  };

  useEffect(() => {
    axiosInstance.get('/user/').then((response) => setManagers(response.data));
    axiosInstance.get('/role/').then((response) => setRoles(response.data));
    axiosInstance.get('/office-branch/').then((response) => setLocations(response.data));
  }, []);

  const handleAssign = (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    
    const requests = [];

    if (managerId) {
      requests.push(axiosInstance.patch(`/assign-manager/${userId}/`, { manager_id: managerId }));
    }
    if (roleId || activeRoleId) {
      requests.push(axiosInstance.patch(`/users/${userId}/assign-roles/`, { role: roleId }));
    }
    if (locationId) {
      requests.push(axiosInstance.patch(`/assign-work-location/${userId}/`, { work_location_id: locationId }));
    }

    Promise.all(requests)
      .then(() => {
        setSuccess('Assignment successful!');
        setTimeout(() => {
          navigate('/employees');
        }, 1500);
      })
      .catch((err) => {
        console.error(err);
        setError('Assignment failed. Please try again.');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleLogout = () => {
    axiosInstance.post('/logout/', { refresh: localStorage.getItem('refresh') })
      .then(() => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('employee_code');
        localStorage.removeItem('assignedRole');
        localStorage.removeItem('id'); 
        window.location.href = '/';
      })
      .catch(console.error);
  }; 

  return (
    <div className={`dashboard-wrapper ${isSidebarVisible ? 'sidebar-open' : 'sidebar-closed'}`}>
      <Header onToggleClick={handleToggleClick} isSidebarVisible={isSidebarVisible} />
      <Sidebar 
        isSidebarVisible={isSidebarVisible} 
        currentUser={currentUser} 
        activeRole={currentUser?.role}
        error={roleError}
        handleSwitchRole={handleSwitchRole}
        handleLogout={handleLogout} 
      />
      
      <main className="dashboard-main-content">
        <div className="dashboard-header-section">
          <h1>👤 Assign Manager, Role & Location</h1>
          <p>Configure employee role, manager, and work location</p>
        </div>

        <div className="assign-card">
          {error && (
            <div className="form-error-message">
              <span>⚠️</span> {error}
            </div>
          )}
          
          {success && (
            <div className="form-success-message">
              <span>✓</span> {success}
            </div>
          )}

          <form onSubmit={handleAssign}>
            <div className="assign-section">
              <div className="section-header">
                <span className="section-icon">🏢</span>
                <h3>Assignment Details</h3>
              </div>

              <div className="assign-form-grid">
                <div className="form-group">
                  <label htmlFor="managerSelect">Manager</label>
                  <select
                    id="managerSelect"
                    value={managerId}
                    onChange={(e) => setManagerId(e.target.value)}
                  >
                    <option value="">Select a Manager</option>
                    {managers.map((manager) => (
                      <option key={manager.id} value={manager.id}>
                        {manager.first_name} {manager.last_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="roleSelect">Role</label>
                  <select
                    id="roleSelect"
                    value={roleId}
                    onChange={(e) => setRoleId(e.target.value)}
                  >
                    <option value="">Select a Role</option>
                    {roles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="locationSelect">Work Location</label>
                  <select
                    id="locationSelect"
                    value={locationId}
                    onChange={(e) => setLocationId(e.target.value)}
                  >
                    <option value="">Select a Work Location</option>
                    {locations.map((location) => (
                      <option 
                        key={location.id} 
                        value={location.id}
                        disabled={location.status === 'inactive'}
                      >
                        {location.name} ({location.status})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button 
                type="submit" 
                className="submit-btn"
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="loading-spinner"></span>
                ) : (
                  <>
                    <span>✓</span> Assign
                  </>
                )}
              </button>
              
              <button 
                type="button" 
                className="cancel-btn"
                onClick={() => navigate('/employees')}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default AssignPage;

